import React, { Component } from 'react';
import * as R from 'ramda';
import StandardButton from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import EditIcon from '@atlaskit/icon/glyph/edit';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import Tooltip from '@atlaskit/tooltip';
import { injectIntl, FormattedMessage } from '@atlassian/jira-intl';
import DropMenu, { DropMenuItem } from '@atlassian/jira-portfolio-3-common/src/drop-menu/index.tsx';
import { REPORT } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/app/types.tsx';
import ScenarioDialog from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/settings/scenarios/scenario-dialog';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import EllipsedWithTooltip from '@atlassian/jira-portfolio-3-portfolio/src/common/view/ellipsed-with-tooltip';
import {
	redirectToSettingsSubPage,
	redirectToNewScenario,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { HistoryAdapter } from '@atlassian/jira-spa-router-adapters/src/controllers/history/index.tsx';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, State } from './types';

// eslint-disable-next-line jira/react/no-class-components
class Scenarios extends Component<Props, State> {
	state = {
		selectedScenarioId: this.props.currentScenarioId,
		isDropMenuOpen: false,
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static getDerivedStateFromProps(props: any, state: any) {
		if (state.selectedScenarioId !== props.currentScenarioId) {
			return {
				...state,
				selectedScenarioId: props.currentScenarioId,
			};
		}

		return null;
	}

	onScenarioChange = (sid: number) => {
		const { currentScenarioId } = this.props;
		if (currentScenarioId === sid) {
			return;
		}
		this.setState(() => ({ selectedScenarioId: sid }));
		redirectToNewScenario(this.props.history, sid);
	};

	onOpenChange = ({ isOpen }: { isOpen: boolean }) => {
		this.setState({
			isDropMenuOpen: isOpen,
		});
	};

	scenarioListRenderer = () => {
		const { selectedScenarioId } = this.state;

		// reordering the scenarios to put the active scenario first
		const scenarios = R.unnest(
			R.partition(R.propEq('id', selectedScenarioId), this.props.planScenarios),
		);

		return scenarios.map(({ color, id, title }) => (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div key={id} className={selectedScenarioId === id ? styles.activeScenarioItem : ''}>
				<DropMenuItem
					elemBefore={
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<div className={styles.scenarioItemColor} style={{ backgroundColor: color }} />
					}
					id={id}
					isSelected={selectedScenarioId === id}
					onClick={() => {
						this.onScenarioChange(id);
					}}
				>
					<EllipsedWithTooltip content={title} id={id}>
						<span>{title}</span>
					</EllipsedWithTooltip>
				</DropMenuItem>
			</div>
		));
	};

	renderScenarioTitle = (scenarioTitle: string, color: undefined | string) => {
		const { mode } = this.props;
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.scenarioReadOnlyWrapper}>
				<Tooltip content={scenarioTitle} position="bottom">
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.scenario}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.scenarioSwatch} style={{ backgroundColor: color }} />
						<div
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={`${styles.scenarioTitle} ${
								mode === REPORT ? ` ${styles.isReportMode}` : ''
							}`}
						>
							{scenarioTitle}
						</div>
					</div>
				</Tooltip>
			</div>
		);
	};

	render() {
		const {
			planScenarios,
			isMultiScenarioEnabled,
			isProcessingRequest,
			createScenario,
			isScenariosDialogOpen,
			toggleDialogVisibility,
			isReadOnly,
			mode,
		} = this.props;
		const { selectedScenarioId, isDropMenuOpen } = this.state;

		if (isMultiScenarioEnabled !== true || !isDefined(planScenarios) || planScenarios.length < 1) {
			return null;
		}

		const selectedScenario = planScenarios.find((scenario) => scenario.id === selectedScenarioId);
		const triggerTitle = selectedScenario ? selectedScenario.title : '';
		const selectedScenarioColor = selectedScenario ? selectedScenario.color : '';

		if (isReadOnly) {
			return this.renderScenarioTitle(triggerTitle, selectedScenarioColor);
		}

		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.scenarioWrapper}>
				<DropMenu
					trigger={({ triggerRef, ...props }) => (
						<Tooltip content={triggerTitle} position="bottom">
							<StandardButton
								iconAfter={<ChevronDownIcon label="" />}
								isSelected={isDropMenuOpen}
								ref={triggerRef}
								{...props}
							>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.scenarioButton}>
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={styles.scenarioSwatch}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={{ backgroundColor: selectedScenarioColor }}
									/>
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles.scenarioTitle} ${
											mode === REPORT ? styles.isReportMode : ''
										}`}
									>
										{triggerTitle}
									</div>
								</div>
							</StandardButton>
						</Tooltip>
					)}
					onOpenChange={this.onOpenChange}
				>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.scenarioSwitcherMenuHeader}>
						<FormattedMessage {...messages.scenarios} />
					</div>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.scenariosList}>{this.scenarioListRenderer()}</div>
					<DropMenuItem
						elemBefore={<AddIcon label="" />}
						onClick={() => {
							toggleDialogVisibility(true);
						}}
					>
						<FormattedMessage {...messages.createScenario} />
					</DropMenuItem>
					<DropMenuItem
						elemBefore={<EditIcon label="" />}
						onClick={() => redirectToSettingsSubPage('scenarios')}
					>
						<FormattedMessage {...messages.manageScenarios} />
					</DropMenuItem>
				</DropMenu>
				{isScenariosDialogOpen && (
					<ScenarioDialog
						isProcessingRequest={isProcessingRequest}
						isMultiScenarioEnabled={isMultiScenarioEnabled}
						createScenario={(payload) => createScenario({ ...payload, redirect: true })}
						onClose={() => {
							toggleDialogVisibility(false);
						}}
					/>
				)}
			</div>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ScenariosWithHistory = (props: any) => (
	// @ts-expect-error - TS2739 - Type '{ children: (history: any) => Element; }' is missing the following properties from type 'RouteContext': location, query, route, match, action
	<HistoryAdapter>{(history) => <Scenarios history={history} {...props} />}</HistoryAdapter>
);

export default injectIntl(ScenariosWithHistory);

export const ScenarioTestComponent = injectIntl(Scenarios);
